import queryString from 'querystring'

import React, { useRef, useState } from 'react'
import { LocationMarkerIcon } from '@heroicons/react/outline'
import Image from 'components/Image'
import Button from 'components/ui/Button'
import {
  GoogleMap,
  InfoWindow,
  LoadScriptNext,
  Marker
} from '@react-google-maps/api'

const MAPS_KEY = 'AIzaSyDBXkdl95KlS2SmNfQfHYQRGMic6USqegw'

export type MapProps = {
  marker: MarkerType
  markerTitle: string
  markerAddress: string
  scale?: number
}

export type MarkerType = {
  lat: number
  lng: number
}
export type MapContainerProps = { height: number }

const Map = ({
  height,
  marker,
  markerTitle,
  markerAddress,
  scale = 2
}: MapProps & MapContainerProps) => {
  const [useDynamicMap, setUseDynamicMap] = useState<boolean>(false)
  const markerRef = useRef<Marker>(null)
  const [isOpen, setIsOpen] = useState(false)

  const staticImage = (
    <Image
      alt="Map of trucks"
      layout="fill"
      objectFit="cover"
      objectPosition="50% 50%"
      quality={50}
      src={`https://maps.googleapis.com/maps/api/staticmap?${queryString.encode(
        {
          center: `${marker.lat},${marker.lng}`,
          zoom: 12,
          size: `${640}x${height}`,
          key: MAPS_KEY,
          scale,
          format: 'jpg',
          markers: `color:red|size:mid|${marker.lat},${marker.lng}`
        }
      )}`}
    />
  )
  const staticMap = (
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus, jsx-a11y/click-events-have-key-events
    <div
      className="cursor-pointer relative rounded-md overflow-hidden"
      onClick={() => setUseDynamicMap(true)}
      role="button"
      style={{ height }}
    >
      {staticImage}
      <div className="flex justify-center items-center h-full relative">
        <div className="absolute bg-black opacity-30 h-full w-full" />
        {!useDynamicMap && (
          <Button
            className="absolute"
            color="primary"
            onClick={() => setUseDynamicMap(true)}
            size="large"
            variant="contained"
            shadow
          >
            <LocationMarkerIcon className="w-5 h-5 mr-1 text-white" />{' '}
            <p className="whitespace-nowrap">Browse Location</p>
          </Button>
        )}
      </div>
    </div>
  )
  if (!useDynamicMap) return staticMap

  return (
    <LoadScriptNext
      googleMapsApiKey={MAPS_KEY}
      googleMapsClientId="google-maps-script-map"
      loadingElement={staticMap}
      mapIds={['roadmap']}
    >
      <div className="rounded-md overflow-hidden">
        <GoogleMap
          center={marker}
          mapContainerStyle={{ height }}
          options={{
            gestureHandling: 'auto'
          }}
          zoom={13}
        >
          <Marker
            ref={markerRef}
            onClick={() => setIsOpen(true)}
            position={marker}
          >
            {isOpen && (
              <InfoWindow
                onCloseClick={() => setIsOpen(false)}
                position={marker}
              >
                <div className="p-2 bg-white">
                  <p className="font-bold text-xl underline cursor-pointer">
                    {markerTitle}
                  </p>
                  <p className="mt-2 italic text-gray-500">{markerAddress}</p>
                </div>
              </InfoWindow>
            )}
          </Marker>
        </GoogleMap>
      </div>
    </LoadScriptNext>
  )
}

export default Map
